import { send } from "../../fetch";
import { PreviewBillingCyclesResponseProto } from "@noom/noom-contracts/noom_contracts/billing/bms/subscriptions/bms_subscription_service";
import { CustomerDetailsProto } from "@noom/noom-contracts/noom_contracts/billing/bms/common/shared";
import { OrderProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/orders_service";
import { getCountryCode, getLanguage, getRouteId } from "../../meristemContext";
import { BillingAddress } from "src/utils/redux/slices/checkout";
import { AddressProto } from "@noom/noom-contracts/noom_contracts/billing/address/address";
import { BillingUserProto } from "@noom/noom-contracts/noom_contracts/billing/user/billing_user";
import { PaymentMethodProto } from "@noom/noom-contracts/noom_contracts/billing/payment_method/payment_method";
import { appConfig } from "src/config";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { PurchaseProgramResponse } from "./checkout";
import getStore from "src/utils/redux/store";
import { getAuthorizationHeader } from "src/api/utils";
import {
  ExecutePurchaseOrderRequestProto_Body,
  ExecutePurchaseOrderResponseProto,
} from "@noom/noom-contracts/noom_contracts/growth/orders/growth_orders_service";
import { PurchaseOperationProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/order_operation";
import { getActiveUser } from "@noom/hive-auth";
import { logDebug } from "src/utils/monitoring/logging";

/**
 * Creates an order for med checkout flow
 */
export async function createOrderRequest(
  accessCode?: string,
  previewMode = false,
  customerDetails?: CustomerDetailsProto,
  purchaseOperation?: PurchaseOperationProto
): Promise<OrderProto> {
  if (!purchaseOperation?.saleItemId) {
    throw new Error("Purchase operation with sale item ID is required");
  }
  if (!previewMode && !accessCode) {
    throw new Error("Access code is required when previewMode is false");
  }
  let apiUrl = `${appConfig.BILLING_CORE_API_URL}/billing/v2/orders`;
  if (previewMode) {
    apiUrl += `?validateOnly=true`;
  }
  const task = trackTask("CreateOrderRequest", {
    previewMode,
    hasCustomerDetails: Boolean(customerDetails),
  });
  task.start();
  try {
    const request: any = {
      operations: [
        {
          purchaseOperation,
        },
      ],
      customerDetails,
    };
    if (!previewMode) {
      request.accessCode = accessCode;
    }
    const response = await send("POST", apiUrl, request, {
      headers: getAuthorizationHeader(),
    });
    task.success();
    return response;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}

export async function previewBillingCycles(params: {
  paymentSchedule: any;
}): Promise<PreviewBillingCyclesResponseProto> {
  const request: any = {
    subscriptionScheduleDetails: {
      paymentScheduleStart: new Date().toISOString(),
      paymentSchedule: params.paymentSchedule,
      isSubscriptionStart: true,
    },
  };

  const task = trackTask("PreviewBillingCyclesRequest", params);
  task.start();
  try {
    const response = await send(
      "POST",
      `${appConfig.BILLING_CORE_API_URL}/bms/v1/subscription/previewBillingCycles`,
      request,
      {
        headers: getAuthorizationHeader(),
      }
    );

    task.success();
    return response;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}

/**
 * Gets an existing order by UUID
 */
export async function getOrderRequest(params: {
  orderUuid: string;
}): Promise<OrderProto> {
  const task = trackTask("GetOrderRequest", params);
  task.start();
  try {
    const response = await send(
      "GET",
      `${appConfig.BILLING_CORE_API_URL}/billing/v2/orders/${params.orderUuid}`,
      null,
      {
        headers: getAuthorizationHeader(),
      }
    );
    task.success();
    return response;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}

/**
 * Creates an address in BCI and returns the created AddressProto
 */
export async function createAddress(params: {
  accessCode: string;
  address?: BillingAddress;
  recipient?: string;
}): Promise<AddressProto> {
  const task = trackTask("CreateBillingAddress", {});
  task.start();
  try {
    const createAddressRequest: AddressProto = {
      recipient: params.recipient,
      addressLine1: params.address?.address1 || "",
      addressLine2: params.address?.address2,
      city: params.address?.city || "",
      zoneCode: params.address?.region || "",
      postalCode: params.address?.zipcode || "",
      countryCode: params.address?.country || getCountryCode(),
    };

    const address: AddressProto = await send(
      "POST",
      `${appConfig.BILLING_CUSTOMERINFO_API_URL}/billing/v2/users/${params.accessCode}/addresses`,
      createAddressRequest,
      {
        headers: getAuthorizationHeader(),
      }
    );
    task.success();
    return address;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}

/**
 * Updates the billing user active payment method in BCI
 */
export async function updateBillingUserRequest({
  accessCode,
  activePaymentMethod,
  preferredShippingAddressId,
  homeAddressId,
}: {
  accessCode: string;
  activePaymentMethod?: Partial<PaymentMethodProto>;
  preferredShippingAddressId?: string;
  homeAddressId?: string;
}): Promise<BillingUserProto | undefined> {
  if (!activePaymentMethod && !preferredShippingAddressId && !homeAddressId) {
    logDebug("UpdateBillingUser: Skipping update");
    return undefined;
  }
  const task = trackTask("UpdateBillingUser", {
    accessCode,
  });
  task.start();
  try {
    const request: Partial<BillingUserProto> = {
      preferredShippingAddressId,
      homeAddressId,
      activePaymentMethod: activePaymentMethod as PaymentMethodProto,
    };
    const updateMask = [
      activePaymentMethod ? "activePaymentMethod" : undefined,
      preferredShippingAddressId ? "preferredShippingAddressId" : undefined,
      homeAddressId ? "homeAddressId" : undefined,
    ]
      .filter(Boolean)
      .join(",");
    const response = await send<BillingUserProto>(
      "PATCH",
      `${appConfig.BILLING_CUSTOMERINFO_API_URL}/billing/v2/users/${accessCode}?updateMask=${updateMask}`,
      request,
      {
        headers: getAuthorizationHeader(),
      }
    );
    task.success();
    return response;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}

export async function executePurchaseOrder(
  orderUuid: string,
  recaptchaToken: string
): Promise<PurchaseProgramResponse> {
  const task = trackTask("ExecutePurchaseOrder", {
    orderUuid,
  });
  task.start();
  try {
    const { userData, checkoutOrders } = getStore().getState();
    const url = "/api/orders/executePurchaseOrder/";
    const body: ExecutePurchaseOrderRequestProto_Body = {
      orderUuid,
      recaptchaToken,
      routeId: getRouteId(),
      language: getLanguage(),
    };
    const response = await send<ExecutePurchaseOrderResponseProto>(
      "POST",
      url,
      body,
      {
        headers: getAuthorizationHeader(),
      }
    );
    const { email, name } = getActiveUser();
    task.success();
    return {
      name,
      email,
      payment_cc_last_4:
        checkoutOrders?.billingUser?.activePaymentMethod?.last4,
      payment_cc_type: checkoutOrders?.billingUser?.activePaymentMethod?.type,
      user_id: userData.user_id,
      eltv_13_months: response.eltvValue,
    } as PurchaseProgramResponse;
  } catch (ex) {
    task.failed(ex);
    throw ex;
  }
}
